/**
 * 🇨🇭 Grenzpaket.ch-Anbindung (Sonderfunktion „grenzpaket", exklusiv für Konto 1186)
 *
 * Verbindet PaketPilot im Hintergrund mit dem Partner-Portal https://shop.grenzpaket.ch:
 *   1. Beim Scannen eines Pakets wird der erkannte Empfänger (über Grenzpaket-Kundennummer z. B. „(200086)"
 *      oder über den Namen) automatisch mit der Grenzpaket-Kundendatenbank abgeglichen.
 *   2. Zeigt Tarif (💶 Barzahlung vs. 💳 Paketfach-Guthaben), Guthaben, interne Kundenhinweise und Pakettyp.
 *   3. Erlaubt den Bestands-Import offener Pakete aus dem Grenzpaket-Rapport (inkl. Regalplatz aus `;P6 - C`).
 *   4. Zeigt bei der Ausgabe mit Unterschrift deutlich an, ob bar kassiert werden muss oder Paketfach-Kredit vorliegt.
 *   5. 🔒 Alle Live-Schreibaktionen an Grenzpaket sind aktuell sichtbar, aber deaktiviert (`disabled`),
 *      da Grenzpaket beim Buchen sofort den Endkunden benachrichtigt.
 */
import { apiFetch } from './apiService';
import { hasCustomFeature } from './customFeatures';

const ON_KEY = 'kiosk_grenzpaket_on';
const DEFAULT_TYPE_KEY = 'kiosk_grenzpaket_default_type';
const DEMO_MODE_KEY = 'kiosk_demo_mode';

export const isDemoModeActive = () => {
  try {
    return localStorage.getItem(DEMO_MODE_KEY) === '1';
  } catch {
    return false;
  }
};

export const setDemoModeActive = (on) => {
  try {
    if (on) localStorage.setItem(DEMO_MODE_KEY, '1');
    else localStorage.removeItem(DEMO_MODE_KEY);
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('kiosk:demo-mode-changed', { detail: { active: !!on } }));
    }
  } catch { /* */ }
};

export const isDemoPackage = (paket) => {
  if (!paket) return false;
  if (paket.isDemo === true) return true;
  const rawNotiz = String(paket.notizen || '').trim();
  if (rawNotiz.includes('[DEMO]') || rawNotiz.includes('"isDemo":true')) return true;
  if (rawNotiz.startsWith('{')) {
    try {
      const obj = JSON.parse(rawNotiz);
      if (obj && obj.isDemo === true) return true;
    } catch { /* ignore */ }
  }
  const z = typeof paket.zusatzinfos === 'string'
    ? (() => { try { return JSON.parse(paket.zusatzinfos); } catch { return null; } })()
    : paket.zusatzinfos;
  if (z && z.isDemo === true) return true;
  return false;
};

export const grenzpaketFreigeschaltet = () => hasCustomFeature('grenzpaket');

export const getDefaultGrenzpaketType = () => {
  try {
    const v = localStorage.getItem(DEFAULT_TYPE_KEY);
    return v === 'PaketKlein' || v === 'Brief' ? v : 'Paket';
  } catch {
    return 'Paket';
  }
};

export const setDefaultGrenzpaketType = (typ) => {
  try {
    const clean = typ === 'PaketKlein' || typ === 'Brief' ? typ : 'Paket';
    localStorage.setItem(DEFAULT_TYPE_KEY, clean);
  } catch { /* */ }
};

// Standardmäßig EIN, sobald das Konto für Grenzpaket freigeschaltet ist (kann im Panel umgeschaltet werden)
export const isGrenzpaketOn = () => {
  try {
    return localStorage.getItem(ON_KEY) !== '0';
  } catch {
    return true;
  }
};

export const setGrenzpaketOn = (on) => {
  try {
    localStorage.setItem(ON_KEY, on ? '1' : '0');
  } catch { /* */ }
};

export const grenzpaketAktiv = () => grenzpaketFreigeschaltet() && isGrenzpaketOn();

const TIMEOUT_MS = 25000;
const api = {
  get: (path, params) => apiFetch(params ? `${path}?${new URLSearchParams(params)}` : path, { method: 'GET', timeout: TIMEOUT_MS }),
  post: (path, data) => apiFetch(path, { method: 'POST', body: JSON.stringify(data), timeout: TIMEOUT_MS }),
};

async function ruf(fn) {
  try {
    return await fn();
  } catch (e) {
    if (e && e.status === 403) return { ok: false, reason: 'nicht_freigeschaltet', message: '' };
    return {
      ok: false,
      reason: 'offline',
      message: (e && e.message) || '',
      status: e && e.status,
    };
  }
}

export const grenzpaketStatus = () => ruf(() => api.get('/grenzpaket/status'));
export const grenzpaketSync = () => ruf(() => api.post('/grenzpaket/sync', {}));
export const grenzpaketUebersicht = (params = {}) => ruf(() => api.get('/grenzpaket/uebersicht', params));
export const grenzpaketCustomerSearch = ({ kundenNr = '', name = '' } = {}) =>
  ruf(() => api.post('/grenzpaket/customer-search', { kundenNr, name }));
export const grenzpaketLogin = (email, password) => ruf(() => api.post('/grenzpaket/login', { email, password }));
export const grenzpaketLogout = () => ruf(() => api.post('/grenzpaket/logout', {}));
export const grenzpaketLog = (limit = 80) => ruf(() => api.get('/grenzpaket/log', { limit }));
export const grenzpaketNotiz = (op, code, extra = {}) => ruf(() => api.post('/grenzpaket/note', { op, code, ...extra }));

/**
 * Prüft, ob eine erkannte Straße die eigene Shop-Adresse der Grenzpaket-Filiale Bad Säckingen ist.
 */
export function istGrenzpaketShopStrasse(str) {
  const s = String(str || '').trim();
  if (!s) return true;
  return /\b(am\s+buchrain|grenzpaket|printfish)\b/i.test(s) || /^KD\s*\d+/i.test(s);
}

/**
 * Extrahiert aus den vom Etikett gelesenen Feldern (Name, Straße, PLZ/Ort) eine evtl. vorhandene
 * Grenzpaket-Kundennummer (z. B. `(200086)` oder `KD 35358`) sowie den bereinigten Empfängernamen.
 */
export function extractGrenzpaketFromLabel({ name = '', streetAndNumber = '', postalAndCity = '' } = {}) {
  const komb = `${name || ''} | ${streetAndNumber || ''} | ${postalAndCity || ''}`;
  let kundenNr = '';

  // 1. Zahl in Klammern: "Simon Kern (200086)"
  const klammerMatch = komb.match(/\(\s*(\d{4,7})\s*\)/);
  if (klammerMatch && klammerMatch[1] !== '79713') {
    kundenNr = klammerMatch[1];
  }

  // 2. Mit Präfix KD / Kundennummer / Grenzpaket / Fach
  if (!kundenNr) {
    const prefixMatch = komb.match(/\b(?:kd|kdnr|kunden(?:nr|nummer)?|gp|grenzpaket|paketfach|postfach)[:.\s#-]*(\d{4,7})\b/i);
    if (prefixMatch && prefixMatch[1] !== '79713') {
      kundenNr = prefixMatch[1];
    }
  }

  // 3. Freistehende 6-stellige Grenzpaket-Kundennummer (beginnt mit 1 oder 2, z. B. 200086) im Namen oder in der Straße
  if (!kundenNr) {
    const nameOderStr = `${name || ''} ${streetAndNumber || ''}`;
    const numMatches = [...nameOderStr.matchAll(/\b([12]\d{5})\b/g)].map((m) => m[1]);
    if (numMatches.length === 1) {
      kundenNr = numMatches[0];
    }
  }

  const cleanName = String(name || '')
    .replace(/\(\s*\d{3,8}\s*\)/g, ' ')
    .replace(/\b(?:kd|kdnr|kunden(?:nr|nummer)?)[:.\s#-]*\d{3,8}\b/gi, ' ')
    .replace(/\b(grenzpaket\s+printfish|grenzpaket|printfish|am\s+buchrain\s*24)\b/gi, ' ')
    .replace(/\b[12]\d{5}\b/g, (m) => (m === kundenNr ? ' ' : m))
    .replace(/\s+/g, ' ')
    .trim();

  return {
    kundenNr,
    cleanName: cleanName || String(name || '').trim(),
  };
}

/**
 * Entfernt eine angehängte Grenzpaket-Kundennummer wie `(200086)` oder `KD 200086` aus dem Namen,
 * z. B. für die reine Namenssuche oder den Etikettendruck (wo die KD-Nr. bereits groß darüber steht).
 */
export function stripGrenzpaketKundenNrFromName(rawName) {
  return String(rawName || '')
    .replace(/\(\s*\d{3,8}\s*\)/g, ' ')
    .replace(/\b(?:kd|kdnr|kunden(?:nr|nummer)?)[:.\s#-]*\d{3,8}\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Formatiert den Empfängernamen bei Grenzpaket immer inklusive Kundennummer:
 *   `formatGrenzpaketName("Simon Kern", "200086")` -> `"Simon Kern (200086)"`
 */
export function formatGrenzpaketName(rawName, kundenNr) {
  const clean = stripGrenzpaketKundenNrFromName(rawName);
  const kd = String(kundenNr || '').replace(/\D+/g, '').trim();
  if (!clean) return kd ? `(${kd})` : '';
  if (!kd || clean.includes(kd)) return clean;
  return `${clean} (${kd})`;
}

/**
 * Erkennt interne Buchhaltungs-Logs der Grenzpaket-Zentrale (`customers_info`, z. B. "08.04.26.lus: alle RE gez...",
 * "RE 328883", "DG von PF48 zu PF24", "Ausgleich Minuskontingent"), damit diese niemals als Shop-Paketnotiz
 * angezeigt oder auf das Etikett gedruckt werden.
 */
export function istZentraleBuchhaltungsLog(str) {
  const s = String(str || '').trim();
  if (!s) return false;
  return (
    /\b\d{1,2}\.\d{2}\.\d{2}\s*\.\s*[a-z]{2,5}\s*:/i.test(s) ||
    /\b(?:RE\s*\d{5,}|Minuskontingent|Ausgleich\s+MK|DG\s+(?:von\s+)?PF\s*\d+|Fehlscan|an\s+Inkasso|Gutschrift\s+da)\b/i.test(s)
  );
}

/**
 * Berechnet die exakten Grenzpaket-Tarif-, Gebühren- und Bon-Details laut `delivery_service.php` & `delivery_quittance.php`:
 *   - Paket (`0`):      Bar = `6,00 CHF / 6,00 EUR`  | Kredit = `1,0 Guthaben`  | Bon: `Paket`
 *   - PaketKlein (`1`): Bar = `3,00 CHF / 3,00 EUR`  | Kredit = `0,5 Guthaben`  | Bon: `Päckchen`
 *   - Brief (`2`):      IMMER Bar = `1,00 CHF / 1,00 EUR bar` (auch bei Kredit-Kunden! B-Code) | Bon: `Brief`
 */
export function getGrenzpaketTarifDetails(meta = {}) {
  const rawTyp = String(meta.paketTyp || 'Paket').trim();
  const paketTyp = ['Paket', 'PaketKlein', 'Brief'].includes(rawTyp) ? rawTyp : 'Paket';
  const rawTarif = meta.tarif === 'Bar' ? 'Bar' : meta.tarif === 'Kredit' ? 'Kredit' : 'Unbekannt';
  const tarif = paketTyp === 'Brief' ? 'Bar' : rawTarif;
  const postboxTyp = paketTyp === 'Brief'
    ? 'Brief'
    : String(meta.postboxTyp || (tarif === 'Bar' ? 'Paketfach-Barzahlung' : tarif === 'Kredit' ? 'Paketfach-Kredit' : '')).trim();
  const bonTypName = paketTyp === 'Brief' ? 'Brief' : paketTyp === 'PaketKlein' ? 'Päckchen' : 'Paket';
  const gebuehrText = paketTyp === 'Brief'
    ? '1,00 CHF / 1,00 EUR bar'
    : tarif === 'Bar'
      ? (paketTyp === 'PaketKlein' ? '3,00 CHF / 3,00 EUR' : '6,00 CHF / 6,00 EUR')
      : (paketTyp === 'PaketKlein' ? '0,5 Guthaben' : '1,0 Guthaben');
  const gebuehrKurz = paketTyp === 'Brief'
    ? '1 CHF / 1 EUR bar'
    : tarif === 'Bar'
      ? (paketTyp === 'PaketKlein' ? '3,00 CHF / 3,00 EUR' : '6,00 CHF / 6,00 EUR')
      : (meta.guthaben != null && meta.guthaben !== '' ? String(meta.guthaben) : '');
  return {
    paketTyp,
    tarif,
    postboxTyp,
    bonTypName,
    gebuehrText,
    gebuehrKurz,
  };
}

/**
 * Formatiert die Anzeigezeile für `streetAndNumber`, damit KD-Nr., Zahlungsart (Bar inkl. Betrag vs. Kredit)
 * und ggf. Pakettyp überall in PaketPilot (Paketliste, Suche, Unterschrift-Dialog) sofort sichtbar sind.
 */
export function formatGrenzpaketStreet(meta = {}) {
  const { tarif, paketTyp, bonTypName, gebuehrText } = getGrenzpaketTarifDetails(meta);
  const teile = [];
  if (meta.kundenNr) teile.push(`KD ${meta.kundenNr}`);
  if (tarif === 'Bar') teile.push(`💶 Bar (${gebuehrText})`);
  else if (tarif === 'Kredit') teile.push('💳 Paketfach (Kredit)');
  else teile.push('⚠️ Tarif prüfen');
  if (paketTyp && paketTyp !== 'Paket') teile.push(bonTypName);
  return teile.join(' · ');
}

/**
 * Baut den JSON-String für `paket.notizen` mit allen Grenzpaket-Metadaten.
 */
export function buildGrenzpaketNote(meta = {}) {
  const { tarif, postboxTyp, paketTyp } = getGrenzpaketTarifDetails(meta);
  const rawInfo = String(meta.info || '').trim();
  const cleanInfo = istZentraleBuchhaltungsLog(rawInfo) ? '' : rawInfo;
  const rawInfoFull = String(meta.infoRaw || '').trim();
  const cleanInfoRaw = istZentraleBuchhaltungsLog(rawInfoFull) ? '' : rawInfoFull;
  const payload = {
    gp: 1,
    kundenNr: String(meta.kundenNr || '').trim(),
    tarif,
    postboxTyp,
    guthaben: meta.guthaben != null ? Number(meta.guthaben) : null,
    paketTyp,
    logId: String(meta.logId || '').trim(),
    logType: String(meta.logType || '').trim(),
    paketCode: String(meta.paketCode || '').trim(),
    info: cleanInfo,
    ...(cleanInfoRaw ? { infoRaw: cleanInfoRaw } : {}),
    ...(meta.isDemo || isDemoModeActive() ? { isDemo: true } : {}),
  };
  return JSON.stringify(payload);
}

/**
 * Liest Grenzpaket-Metadaten aus einem Paket-Objekt (`notizen`-JSON oder `streetAndNumber`).
 */
export function parseGrenzpaketMeta(paket) {
  if (!paket) return null;
  const rawNotiz = String(paket.notizen || '').trim();
  if (rawNotiz.startsWith('{')) {
    try {
      const obj = JSON.parse(rawNotiz);
      if (obj && obj.gp === 1) {
        const rawInfo = String(obj.info || '').trim();
        const rawInfoFull = String(obj.infoRaw || '').trim();
        const { tarif, postboxTyp, paketTyp } = getGrenzpaketTarifDetails(obj);
        return {
          kundenNr: String(obj.kundenNr || ''),
          tarif,
          postboxTyp,
          guthaben: obj.guthaben != null ? Number(obj.guthaben) : null,
          paketTyp,
          logId: String(obj.logId || ''),
          logType: String(obj.logType || ''),
          paketCode: String(obj.paketCode || ''),
          info: istZentraleBuchhaltungsLog(rawInfo) ? '' : rawInfo,
          infoRaw: istZentraleBuchhaltungsLog(rawInfoFull) ? '' : rawInfoFull,
        };
      }
    } catch { /* fallback below */ }
  }
  if (!grenzpaketFreigeschaltet()) return null;
  const str = String(paket.streetAndNumber || '');
  const kdMatch = str.match(/\bKD\s*(\d{3,8})\b/i);
  if (kdMatch) {
    const isBrief = /\bbrief\b/i.test(str);
    const isKlein = /\b(?:paketklein|p[äa]ckchen)\b/i.test(str);
    const isBar = isBrief || /bar/i.test(str);
    const isKredit = /kredit|paketfach/i.test(str) && !isBar;
    const paketTyp = isBrief ? 'Brief' : isKlein ? 'PaketKlein' : 'Paket';
    return {
      kundenNr: kdMatch[1],
      tarif: isBar ? 'Bar' : isKredit ? 'Kredit' : 'Unbekannt',
      postboxTyp: isBrief ? 'Brief' : isBar ? 'Paketfach-Barzahlung' : isKredit ? 'Paketfach' : '',
      guthaben: null,
      paketTyp,
      logId: '',
      logType: '',
      paketCode: '',
      info: '',
      infoRaw: '',
    };
  }
  return null;
}

const TEXTE = {
  de: {
    titel: '🇨🇭 Grenzpaket.ch-Anbindung',
    menu: '🇨🇭 Grenzpaket.ch (Bad Säckingen)',
    beschreibung: 'Verbindet PaketPilot im Hintergrund mit shop.grenzpaket.ch: automatischer Kunden-Abgleich beim Scannen (KD-Nr., Barzahler vs. Paketfach-Kredit), Bestands-Import offener Pakete und Zahlungs-Hinweis bei der Unterschrift.',
    status: 'Verbindungsstatus',
    pruefen: 'Neu prüfen',
    laedt: 'Wird geprüft …',
    stOk: 'Verbunden mit shop.grenzpaket.ch',
    stLogin: 'Nicht angemeldet — bitte Zugangsdaten prüfen',
    stGesperrt: 'Für dieses Konto nicht freigeschaltet',
    stOffline: 'Keine Verbindung zum Server',
    filiale: 'Filiale',
    anmelden: 'Zugangsdaten (shop.grenzpaket.ch)',
    email: 'E-Mail (z. B. saeckingen@grenzpaket.ch)',
    passwort: 'Passwort',
    uebertragen: 'Anmelden & speichern',
    meldetAn: 'Anmeldung läuft …',
    loginOk: 'Erfolgreich bei shop.grenzpaket.ch angemeldet.',
    loginFehler: 'Anmeldung fehlgeschlagen',
    schalter: 'Grenzpaket-Kundenabgleich auf diesem Gerät aktiv',
    schalterHinweis: 'Gleicht beim Scannen eines Pakets automatisch den Namen oder die Kundennummer mit der Grenzpaket-Datenbank ab und zeigt Zahlungsart (Barzahlung / Paketfach-Kredit) bei der Ausgabe mit Unterschrift.',
    bestandTitel: 'Offener Bestand bei Grenzpaket (Rapport)',
    bestandLaden: 'Rapport neu laden',
    bestandImportBtn: '📥 {n} offene Pakete in PaketPilot übernehmen',
    bestandAlleDa: '✅ Alle angezeigten Pakete aus dem Grenzpaket-Rapport sind bereits in PaketPilot vorhanden.',
    bestandImportiert: '✅ {n} Paket(e) aus Grenzpaket übernommen!',
    liveTitel: 'Live-Buchung bei Grenzpaket (gesperrt)',
    liveSchutzHinweis: '🔒 Sicherheitssperre aktiv: Sobald bei Grenzpaket ein Paket gebucht / ein Etikett erstellt wird, erhält der Kunde sofort eine E-Mail/SMS-Benachrichtigung. Deshalb sind alle Schreib-Aktionen aktuell noch nicht antippbar.',
    liveAnnahme: 'Bei Paket-Annahme automatisch bei Grenzpaket einbuchen & Kunden benachrichtigen',
    liveLager: 'Gescannten Regalplatz automatisch als Lagerort an Grenzpaket übertragen',
    liveAusgabe: 'Bei Paket-Ausgabe mit Unterschrift automatisch bei Grenzpaket ausbuchen',
    protokoll: 'Protokoll',
    protokollLeer: 'Noch keine Vorgänge.',
    neuLaden: 'Aktualisieren',
    schliessen: 'Schließen',
    customerMatch: 'Grenzpaket-Kundenabgleich',
    customerSearching: 'Suche in Grenzpaket-DB …',
    customerNone: 'Kein Treffer in Grenzpaket-DB',
    customerNo: 'KD-Nr.',
    tarifBar: '💶 Barzahlung',
    tarifKredit: 'Paketfach (Kredit)',
    typePaket: 'Paket',
    typeKlein: 'PaketKlein',
    typeBrief: 'Brief',
    guthaben: 'Guthaben',
  },
  en: {
    titel: '🇨🇭 Grenzpaket.ch Integration',
    menu: '🇨🇭 Grenzpaket.ch (Bad Säckingen)',
    beschreibung: 'Connects ParcelPilot with shop.grenzpaket.ch in the background: automatic customer matching on scan (customer ID, cash vs. credit), open stock import, and payment badge on signature handover.',
    status: 'Connection status',
    pruefen: 'Check again',
    laedt: 'Checking …',
    stOk: 'Connected to shop.grenzpaket.ch',
    stLogin: 'Not logged in — please check credentials',
    stGesperrt: 'Not enabled for this account',
    stOffline: 'No connection to server',
    filiale: 'Branch',
    anmelden: 'Credentials (shop.grenzpaket.ch)',
    email: 'Email (e.g. saeckingen@grenzpaket.ch)',
    passwort: 'Password',
    uebertragen: 'Sign in & save',
    meldetAn: 'Signing in …',
    loginOk: 'Successfully signed in to shop.grenzpaket.ch.',
    loginFehler: 'Sign-in failed',
    schalter: 'Enable Grenzpaket customer matching on this device',
    schalterHinweis: 'Automatically matches scanned recipient names or customer numbers against the Grenzpaket database and shows payment type (cash / credit) on signature handover.',
    bestandTitel: 'Open stock at Grenzpaket (Rapport)',
    bestandLaden: 'Reload rapport',
    bestandImportBtn: '📥 Import {n} open parcels into ParcelPilot',
    bestandAlleDa: '✅ All displayed parcels from Grenzpaket Rapport are already in ParcelPilot.',
    bestandImportiert: '✅ Imported {n} parcel(s) from Grenzpaket!',
    liveTitel: 'Live booking at Grenzpaket (locked)',
    liveSchutzHinweis: '🔒 Safety lock active: Booking a parcel at Grenzpaket immediately sends an email/SMS notification to the customer. All write actions are currently disabled.',
    liveAnnahme: 'Automatically book parcel intake at Grenzpaket & notify customer',
    liveLager: 'Automatically sync scanned shelf barcode to Grenzpaket',
    liveAusgabe: 'Automatically check out parcel at Grenzpaket on signature handover',
    protokoll: 'Log',
    protokollLeer: 'No entries yet.',
    neuLaden: 'Refresh',
    schliessen: 'Close',
    customerMatch: 'Grenzpaket Customer Match',
    customerSearching: 'Searching Grenzpaket DB …',
    customerNone: 'No match in Grenzpaket DB',
    customerNo: 'Cust. ID',
    tarifBar: '💶 Cash payment',
    tarifKredit: 'Postbox (Credit)',
    typePaket: 'Parcel',
    typeKlein: 'Small',
    typeBrief: 'Letter',
    guthaben: 'Credit',
  },
};

export function gt(lang = 'de') {
  const dict = TEXTE[lang] || TEXTE.de;
  const fn = (key, vars = {}) => {
    let s = dict[key] || TEXTE.de[key] || key;
    for (const [k, v] of Object.entries(vars)) {
      s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v ?? ''));
    }
    return s;
  };
  return Object.assign(fn, TEXTE.de, dict);
}

